import React, { useState, useMemo } from 'react';
import { Button, Form, Row, Col, Table, Card, Spinner, Modal, Nav, Tab, Alert, Badge } from 'react-bootstrap';
import { useConfigBasedTimeFilter, getStatusText, getToggleButtonText } from '../../utils/timeFilter';
import { useTemplates } from '../../services/api/hooks/useTemplates';
import { useWelcomeTemplate } from '../../services/api/hooks/useWelcomeTemplate';
import { useTopics } from '../../services/api/hooks/useTopics';
import { EmailTemplate } from '../../services/api/types';


const EmailTemplates = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState<number | undefined>(undefined);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    htmlContent: '',
    textContent: '',
    topic_id: undefined as number | undefined
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [searchTerm, setSearchTerm] = useState('');
  const [isReadonly, setIsReadonly] = useState(false);
  const [testEmailSuccess, setTestEmailSuccess] = useState<string[]>([]);
  const { showAll: showAllTemplates, setShowAll: setShowAllTemplates, timeFilter, isLoading: timeFilterLoading } = useConfigBasedTimeFilter();
  const { data: welcomeTemplate, loading: welcomeLoading, refetch: refetchWelcome } = useWelcomeTemplate();
  const { data: topics } = useTopics();

  const { 
    data: templates, 
    loading: isLoading, 
    createTemplate, 
    updateTemplate, 
    deleteTemplate, 
    getTemplate,
    sendTestEmail: sendTestEmailAPI 
  } = useTemplates({ showAll: showAllTemplates, topic_id: selectedTopicId });

  const usedTemplateIds = new Set<number>(
    templates.filter(t => t.isUsedByCampaign).map(t => t.id)
  );

  const otherTemplates = templates.filter(t => t.name !== 'welcome');



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Template name is required.');
      return;
    }
    if (!formData.subject.trim()) {
      alert('Subject is required for email campaigns.');
      return;
    }
    if (!formData.htmlContent.trim()) {
      alert('HTML content is required.');
      return;
    }
    if (!formData.textContent.trim()) {
      alert('Text content is required.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, formData);
      } else {
        await createTemplate(formData);
      }
      handleCloseModal();
      // Refresh welcome template if it was created/updated
      if (formData.name === 'welcome') {
        refetchWelcome();
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (template: EmailTemplate) => {
    try {
      const fullTemplate = await getTemplate(template.id);
      
      setEditingTemplate(fullTemplate);
      setFormData({
        name: fullTemplate.name,
        subject: fullTemplate.subject,
        htmlContent: fullTemplate.htmlContent || '',
        textContent: fullTemplate.textContent || '',
        topic_id: fullTemplate.topic_id
      });
      setIsReadonly(false);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching template details:', error);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (window.confirm(`Are you sure you want to delete template "${name}"?`)) {
      try {
        await deleteTemplate(id);
        // Refresh welcome template if it was deleted
        if (name === 'welcome') {
          refetchWelcome();
        }
      } catch (error) {
        console.error('Error deleting template:', error);
        alert('Failed to delete template');
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData({ name: '', subject: '', htmlContent: '', textContent: '', topic_id: undefined });
    setActiveTab('edit');
    setIsReadonly(false);
  };

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    setFormData({ name: '', subject: '', htmlContent: '', textContent: '', topic_id: 1 });
    setActiveTab('edit');
    setIsReadonly(false);
    setShowModal(true);
  };

  const handleCopyTemplate = async (template: EmailTemplate) => {
    try {
      const fullTemplate = await getTemplate(template.id);
      
      setEditingTemplate(null);
      setFormData({
        name: `Copy of ${fullTemplate.name}`,
        subject: fullTemplate.subject,
        htmlContent: fullTemplate.htmlContent || '',
        textContent: fullTemplate.textContent || '',
        topic_id: fullTemplate.topic_id
      });
      setActiveTab('edit');
      setIsReadonly(false);
      setShowModal(true);
    } catch (error) {
      console.error('Error copying template:', error);
    }
  };

  const handleView = async (template: EmailTemplate) => {
    try {
      const fullTemplate = await getTemplate(template.id);
      
      setEditingTemplate(fullTemplate);
      setFormData({
        name: fullTemplate.name,
        subject: fullTemplate.subject,
        htmlContent: fullTemplate.htmlContent || '',
        textContent: fullTemplate.textContent || '',
        topic_id: fullTemplate.topic_id
      });
      setActiveTab('edit');
      setIsReadonly(true);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching template details:', error);
    }
  };





  const handleSendTestFromModal = async () => {
    if (!testEmail || !formData.subject || (!formData.htmlContent && !formData.textContent)) return;
    
    setIsSendingTest(true);
    try {
      // Parse comma-separated emails
      const emails = testEmail.split(',').map(email => email.trim()).filter(email => email);
      
      await sendTestEmailAPI({ 
        emails: emails,
        subject: formData.subject,
        htmlContent: formData.htmlContent,
        textContent: formData.textContent
      });
      
      setTestEmailSuccess(emails);
      setTestEmail('');
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Error sending test email');
    } finally {
      setIsSendingTest(false);
    }
  };

  const openEmailReport = (email: string) => {
    const reportUrl = `${window.location.origin}/reports?tab=email-search&email=${encodeURIComponent(email)}`;
    window.open(reportUrl, '_blank', 'noopener,noreferrer');
  };

  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return otherTemplates;
    return otherTemplates.filter(template => 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [otherTemplates, searchTerm]);



  return (
    <div className="m-3">
      <Card className="mb-3">
        <Card.Header as="h5" className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#fff3cd' }}>
          <span>Welcome Email Template</span>
          {!welcomeTemplate && (
            <Button variant="success" size="sm" onClick={() => {
              setFormData({ name: 'welcome', subject: 'Welcome!', htmlContent: '', textContent: '', topic_id: 1 });
              setEditingTemplate(null);
              setActiveTab('edit');
              setIsReadonly(false);
              setShowModal(true);
            }}>
              Create Welcome Template
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          <div className="alert alert-warning mb-3">
            <strong>Welcome Email Template:</strong> This template is automatically sent to new subscribers when using the API with <code>sendWelcomeEmail: true</code>. 
            The template must be named exactly "welcome" to work properly.
          </div>
          {welcomeLoading ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" />
            </div>
          ) : welcomeTemplate ? (
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>Name</th>
                  <th style={{ width: '35%' }}>Subject</th>
                  <th style={{ width: '15%' }}>Created</th>
                  <th style={{ width: '25%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{welcomeTemplate.name}</td>
                  <td 
                    title={welcomeTemplate.subject}
                    style={{ 
                      maxWidth: '250px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}
                  >
                    {welcomeTemplate.subject}
                  </td>
                  <td>{new Date(welcomeTemplate.createdAt).toLocaleDateString()}</td>
                  <td>
                    <Button size="sm" variant="outline-info" onClick={() => handleView(welcomeTemplate)} className="me-1">
                      View
                    </Button>
                    <Button size="sm" variant="outline-success" onClick={() => handleCopyTemplate(welcomeTemplate)} className="me-1">
                      Copy
                    </Button>
                    {!usedTemplateIds.has(welcomeTemplate.id) && (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          onClick={() => handleEdit(welcomeTemplate)} 
                          className="me-1"
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-danger" 
                          onClick={() => handleDelete(welcomeTemplate.id, welcomeTemplate.name)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">No welcome template found. Create one using the button above to enable automatic welcome emails for new subscribers.</p>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header as="h5" className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e8f5e8' }}>
          <div className="d-flex align-items-center gap-3">
            <span>Templates</span>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">
                {getStatusText(showAllTemplates, timeFilter)}
              </span>
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => setShowAllTemplates(!showAllTemplates)}
                disabled={timeFilterLoading}
              >
                {getToggleButtonText(showAllTemplates, timeFilter)}
              </Button>
            </div>
          </div>
          <Button variant="primary" onClick={handleNewTemplate}>
            New Template
          </Button>
        </Card.Header>
        <Card.Body>
          <div className="alert alert-info mb-3">
            <strong>Note:</strong> Templates used in campaigns cannot be edited or deleted. Use the "Copy" button to create a new version.
          </div>
          <Form className="mb-3">
            <Row className="mb-2">
              <Col md={6}>
                <Form.Control
                    type="text"
                    placeholder="Search templates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
              </Col>
              <Col md={6}>
                <Form.Select
                  value={selectedTopicId || ''}
                  onChange={(e) => setSelectedTopicId(e.target.value ? parseInt(e.target.value) : undefined)}
                >
                  <option value="">All Topics</option>
                  {topics.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            {(searchTerm || selectedTopicId) && (
              <div className="d-flex gap-2 flex-wrap">
                {searchTerm && (
                  <Badge bg="primary" className="d-flex align-items-center gap-1">
                    Search: {searchTerm}
                    <i
                      className="bi bi-x cursor-pointer"
                      onClick={() => setSearchTerm('')}
                      style={{ cursor: 'pointer' }}
                    />
                  </Badge>
                )}
                {selectedTopicId && (
                  <Badge bg="secondary" className="d-flex align-items-center gap-1">
                    Topic: {topics.find(t => t.id === selectedTopicId)?.name}
                    <i
                      className="bi bi-x cursor-pointer"
                      onClick={() => setSelectedTopicId(undefined)}
                      style={{ cursor: 'pointer' }}
                    />
                  </Badge>
                )}
                <Button
                  variant="link"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedTopicId(undefined);
                  }}
                  className="text-muted p-0"
                >
                  Clear all
                </Button>
              </div>
            )}
          </Form>
          {isLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <p>{otherTemplates.length === 0 ? 'No templates found. Create one above!' : 'No templates match your search.'}</p>
          ) : (
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>Name</th>
                  <th style={{ width: '35%' }}>Subject</th>
                  <th style={{ width: '15%' }}>Created</th>
                  <th style={{ width: '25%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <tr key={template.id}>
                    <td 
                      title={template.name}
                      style={{ 
                        maxWidth: '200px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                      }}
                    >
                      {template.name}
                    </td>
                    <td 
                      title={template.subject}
                      style={{ 
                        maxWidth: '250px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                      }}
                    >
                      {template.subject}
                    </td>
                    <td>{new Date(template.createdAt).toLocaleDateString()}</td>
                    <td>
                      <Button size="sm" variant="outline-info" onClick={() => handleView(template)} className="me-1">
                        View
                      </Button>
                      <Button size="sm" variant="outline-success" onClick={() => handleCopyTemplate(template)} className="me-1">
                        Copy
                      </Button>
                      {!usedTemplateIds.has(template.id) && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline-primary" 
                            onClick={() => handleEdit(template)} 
                            className="me-1"
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline-danger" 
                            onClick={() => handleDelete(template.id, template.name)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        restoreFocus={false}
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{isReadonly ? 'View Template' : (editingTemplate ? 'Edit Template' : 'New Template')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="alert alert-info">
              <strong>Note:</strong> You do not need to manually add tracking for clicks or opens.
              All links (<code>&lt;a href="..."&gt;</code>) will automatically be replaced with tracking links to record clicks,
              and a hidden 1x1 pixel will be added at the end to record opens.
              <br /><br />
              <strong>Personalization:</strong> Use shortcodes like <code>{'{{name}}'}</code>, <code>{'{{email}}'}</code>, <code>{'{{phone}}'}</code>, <code>{'{{countryCode}}'}</code>, <code>{'{{entryDate}}'}</code>, <code>{'{{Custom:lexulous_id}}'}</code>, <code>{'{{Custom:member_since}}'}</code>, <code>{'{{Custom:username}}'}</code>, <code>{'{{unsubscribe_url}}'}</code>, <code>{'{{lastOpened}}'}</code>, <code>{'{{rawData}}'}</code>, <code>{'{{campaign_id}}'}</code>, <code>{'{{date_time}}'}</code>, <code>{'{{email_sha256}}'}</code> to personalize emails with subscriber data, unsubscribe links, and campaign-level metadata.
            </div>
            <Tab.Container
              key={`tab-${showModal}-${editingTemplate?.id || 'new'}`}
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || 'edit')}
            >
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="edit">Edit</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="preview">Preview</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="test">Send Test</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="edit">
                  <Row className="mb-3">
                    <Col>
                      <Form.Label>Template Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="templateName"
                        data-testid="template-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        isInvalid={!formData.name.trim()}
                        readOnly={isReadonly}
                      />
                      <Form.Control.Feedback type="invalid">
                        Template name is required.
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Label>Topic</Form.Label>
                      <Form.Select
                        value={formData.topic_id || ''}
                        onChange={(e) => setFormData({ ...formData, topic_id: e.target.value ? parseInt(e.target.value) : undefined })}
                        disabled={isReadonly}
                      >
                        <option value="">Select Topic (Optional)</option>
                        {topics.map((topic) => (
                          <option key={topic.id} value={topic.id}>
                            {topic.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Label>Subject *</Form.Label>
                      <Form.Control
                        type="text"
                        name="subject"
                        data-testid="template-subject-input"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        required
                        isInvalid={!formData.subject.trim()}
                        readOnly={isReadonly}
                      />
                      <Form.Control.Feedback type="invalid">
                        Subject is required for email campaigns.
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Label>HTML Content *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={10}
                        name="htmlContent"
                        data-testid="template-html-input"
                        value={formData.htmlContent}
                        onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
                        required
                        isInvalid={!formData.htmlContent.trim()}
                        readOnly={isReadonly}
                      />
                      <Form.Control.Feedback type="invalid">
                        HTML content is required.
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Label>Text Content *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={10}
                        name="textContent"
                        data-testid="template-text-input"
                        value={formData.textContent}
                        onChange={(e) => setFormData({ ...formData, textContent: e.target.value })}
                        placeholder="Plain text version of the email"
                        required
                        isInvalid={!formData.textContent.trim()}
                        readOnly={isReadonly}
                      />
                      <Form.Control.Feedback type="invalid">
                        Text content is required.
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  {!isReadonly && (
                    <Row className="mb-3">
                      <Col>
                        <div className="alert alert-light border">
                          <strong>Available Shortcodes:</strong>
                        <div className="mt-2">
                          {[
                            { code: '{{name}}', desc: 'Subscriber name' },
                            { code: '{{email}}', desc: 'Subscriber email' },
                            { code: '{{phone}}', desc: 'Subscriber phone' },
                            { code: '{{source}}', desc: 'Subscriber source' },
                            { code: '{{countryCode}}', desc: 'Country code' },
                            { code: '{{entryDate}}', desc: 'Entry date' },
                            { code: '{{unsubscribe_url}}', desc: 'Unsubscribe link' },
                            { code: '{{lastOpened}}', desc: 'Last opened timestamp (click time)' },
                            { code: '{{rawData}}', desc: 'Full tracking token (click time) — forwards the token into the destination URL so the landing page can identify the subscriber. Contains the plaintext email address, so the destination site will see it in its logs. Only use with trusted destinations.' },
                            { code: '{{campaign_id}}', desc: 'Campaign ID (same for all emails in a campaign)' },
                            { code: '{{date_time}}', desc: 'Unix timestamp of scheduled send time, falls back to creation time for immediate sends (same for all emails)' },
                            { code: '{{email_sha256}}', desc: 'SHA-256 of subscriber email (trimmed and lowercased before hashing)' },
                            { code: '{{UNIQUEKEY}}', desc: 'Unique subscriber ID (per-email identifier)' },
                            { code: '{{MD5EMAIL}}', desc: 'MD5 hash of subscriber email' },
                            { code: '{{SHA1EMAIL}}', desc: 'SHA1 hash of subscriber email' },
                            { code: '{{Custom:lexulous_id}}', desc: 'Lexulous member ID (from subscriber metadata)' },
                            { code: '{{Custom:member_since}}', desc: 'Membership start date (from subscriber metadata)' },
                            { code: '{{Custom:username}}', desc: 'Subscriber username (from subscriber metadata)' }
                          ].map((shortcode, index) => (
                            <span key={index} className="me-3 mb-2 d-inline-block">
                              <code 
                                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                onClick={() => {
                                  const textarea = document.querySelector('textarea[rows="10"]') as HTMLTextAreaElement;
                                  if (textarea) {
                                    const start = textarea.selectionStart;
                                    const end = textarea.selectionEnd;
                                    const newContent = formData.htmlContent.substring(0, start) + shortcode.code + formData.htmlContent.substring(end);
                                    setFormData({ ...formData, htmlContent: newContent });
                                    setTimeout(() => {
                                      textarea.focus();
                                      textarea.setSelectionRange(start + shortcode.code.length, start + shortcode.code.length);
                                    }, 0);
                                  }
                                }}
                                title={`Click to insert: ${shortcode.desc}`}
                              >
                                {shortcode.code}
                              </code>
                            </span>
                          ))}
                        </div>
                          <small className="text-muted">Click any shortcode to insert it at cursor position</small>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Tab.Pane>
                <Tab.Pane eventKey="preview">
                  <div className="mb-3">
                    <strong>Subject:</strong> {formData.subject || 'No subject'}
                  </div>
                  <div className="border p-3" style={{ minHeight: '300px', maxHeight: '400px', overflow: 'auto' }}>
                    {formData.htmlContent ? (
                      <iframe
                        title="Email template preview"
                        srcDoc={formData.htmlContent}
                        style={{ 
                          width: '100%', 
                          minHeight: '280px', 
                          border: 'none',
                          backgroundColor: 'white'
                        }}
                        sandbox="allow-same-origin"
                      />
                    ) : (
                      <p className="text-muted">No content to preview</p>
                    )}
                  </div>
                </Tab.Pane>
                <Tab.Pane eventKey="test">
                  {testEmailSuccess.length > 0 && (
                    <Alert 
                      variant="success" 
                      dismissible 
                      onClose={() => setTestEmailSuccess([])}
                      className="mb-3"
                    >
                      <Alert.Heading>Mail queued successfully!</Alert.Heading>
                      <p className="mb-2">
                        Test emails sent to <strong>{testEmailSuccess.length}</strong> recipient(s). 
                        You can check the delivery status and tracking information.
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {testEmailSuccess.map((email, index) => (
                          <Button 
                            key={index}
                            variant="outline-success" 
                            size="sm"
                            onClick={() => openEmailReport(email)}
                          >
                            {email} →
                          </Button>
                        ))}
                      </div>
                    </Alert>
                  )}
                  <div className="alert alert-light border mb-3">
                    <strong>📧 Testing Services:</strong>
                    <div className="mt-2">
                      <a href="https://aboutmyemail.com/" target="_blank" rel="noopener noreferrer" className="me-3 text-decoration-none">
                        About My Email
                      </a>
                      <a href="https://www.mail-tester.com/" target="_blank" rel="noopener noreferrer" className="me-3 text-decoration-none">
                        Mail Tester
                      </a>
                      <a href="https://www.mailgenius.com/" target="_blank" rel="noopener noreferrer" className="me-3 text-decoration-none">
                        Mail Genius
                      </a>
                      <a href="https://www.unspam.email/" target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                        Unspam Email
                      </a>
                    </div>
                    <small className="text-muted">Use these services to test your email deliverability and spam score</small>
                  </div>
                  <div className="mb-3">
                    <Form.Label>Test Email Addresses</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test1@example.com, test2@example.com, test3@example.com"
                    />
                    <Form.Text className="text-muted">
                      Enter multiple email addresses separated by commas
                    </Form.Text>
                    <Form.Text className="text-info">
                      <strong>📧 Test Mode:</strong> Test emails are isolated from production analytics. 
                      They use campaign name "test-draft-timestamp" and won't affect real campaign statistics, 
                      unsubscribe actions, click tracking, or read statistics.
                    </Form.Text>
                  </div>
                  <div className="mb-3">
                    <strong>Subject:</strong> {formData.subject || 'No subject'}
                  </div>
                  <div className="border p-3" style={{ minHeight: '200px', maxHeight: '300px', overflow: 'auto' }}>
                    {formData.htmlContent ? (
                      <iframe
                        title="Email template test preview"
                        srcDoc={formData.htmlContent}
                        style={{ 
                          width: '100%', 
                          minHeight: '180px', 
                          border: 'none',
                          backgroundColor: 'white'
                        }}
                        sandbox="allow-same-origin"
                      />
                    ) : (
                      <p className="text-muted">No content to send</p>
                    )}
                  </div>
                  <div className="mt-3">
                    <Button 
                      variant="success" 
                      onClick={handleSendTestFromModal}
                      disabled={!testEmail || !formData.subject || (!formData.htmlContent && !formData.textContent) || isSendingTest}
                    >
                      {isSendingTest ? <Spinner animation="border" size="sm" /> : 'Send Test Email'}
                    </Button>
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              {isReadonly ? 'Close' : 'Cancel'}
            </Button>
            {!isReadonly && (
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Save'}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>


    </div>
  );
};

export default EmailTemplates;
